import { useState } from "react";
import { api, type AddressBook, type Named, type Paged, type User } from "../lib/api.js";
import { Drawer, Empty, Notice, useAsync } from "../components/ui.js";
import type { PageProps } from "./Shell.js";

const RULE_LABEL: Record<number, string> = { 1: "Read only", 2: "Read and write", 3: "Full control" };

export default function AddressBooks({ me, onToggleRail }: PageProps) {
  const books = useAsync(() => api.get<AddressBook[]>("/api/console/address-books"), []);
  const users = useAsync(() => api.get<Paged<User>>("/api/console/users?pageSize=500"), []);
  const groups = useAsync(() => api.get<Named[]>("/api/console/groups"), []);
  const [sharing, setSharing] = useState<AddressBook | null>(null);
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const canWrite = me.permissions.includes("*") || me.permissions.includes("ab:write");

  async function create() {
    setError("");
    try { await api.post("/api/console/address-books", { name }); setName(""); books.reload(); }
    catch (e) { setError((e as Error).message); }
  }

  return (
    <>
      <header className="topbar">
        <button className="btn btn--ghost btn--sm" onClick={onToggleRail} aria-label="Menu">&#9776;</button>
        <div className="topbar__title">
          <h1>Address books</h1>
          <span className="topbar__hint">{books.data?.length ?? 0} books</span>
        </div>
      </header>

      <div className="content">
        <div className="stack">
          {error ? <Notice>{error}</Notice> : null}
          <div className="notice">
            Every person has a personal address book. Shared books let a team keep one list of
            machines, with per-person or per-group access.
          </div>

          {!books.loading && !books.data?.length ? (
            <Empty title="No address books" />
          ) : (
            <div className="panel">
              <div className="tablewrap">
                <table className="data">
                  <thead><tr><th>Name</th><th>Kind</th><th>Owner</th><th className="num">Machines</th><th>Shared with</th><th style={{ width: 140 }} /></tr></thead>
                  <tbody>
                    {(books.data ?? []).map((b) => (
                      <tr key={b.guid}>
                        <td>{b.name}</td>
                        <td><span className="tag">{b.personal ? "Personal" : "Shared"}</span></td>
                        <td className="muted">{b.owner_name || <span className="faint">&mdash;</span>}</td>
                        <td className="num">{b.peer_count}</td>
                        <td className="muted small">
                          {b.rules.length ? `${b.rules.length} rule${b.rules.length === 1 ? "" : "s"}` : <span className="faint">Not shared</span>}
                        </td>
                        <td className="actions">
                          {canWrite && !b.personal ? (
                            <>
                              <button className="btn btn--sm" onClick={() => setSharing(b)}>Share</button>
                              <button className="btn btn--danger btn--sm"
                                onClick={() => api.del(`/api/console/address-books/${b.guid}`).then(books.reload)}>
                                Delete
                              </button>
                            </>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {canWrite ? (
            <div className="panel"><div className="panel__body">
              <div className="row row--wrap" style={{ alignItems: "flex-end" }}>
                <div className="field" style={{ marginBottom: 0, flex: "1 1 240px" }}>
                  <label className="field__label">New shared address book</label>
                  <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Field engineers" />
                </div>
                <button className="btn btn--primary" onClick={create} disabled={!name.trim()}>Create</button>
              </div>
            </div></div>
          ) : null}
        </div>
      </div>

      {sharing ? (
        <ShareDrawer
          book={sharing}
          users={users.data?.data ?? []}
          groups={groups.data ?? []}
          onClose={() => setSharing(null)}
          onChanged={books.reload}
        />
      ) : null}
    </>
  );
}

function ShareDrawer({
  book, users, groups, onClose, onChanged,
}: {
  book: AddressBook; users: User[]; groups: Named[];
  onClose: () => void; onChanged: () => void;
}) {
  const [target, setTarget] = useState("");
  const [rule, setRule] = useState(1);
  const [error, setError] = useState("");
  const [rules, setRules] = useState(book.rules);

  async function add() {
    setError("");
    const [kind, id] = target.split(":");
    try {
      const res = await api.post<{ id: string }>(`/api/console/address-books/${book.guid}/rules`, {
        [kind === "u" ? "user_id" : "group_id"]: id, rule,
      });
      setRules((r) => [...r, {
        id: res.id, rule,
        user_id: kind === "u" ? id! : null,
        group_id: kind === "g" ? id! : null,
      }]);
      onChanged();
    } catch (e) { setError((e as Error).message); }
  }

  async function remove(id: string) {
    try {
      await api.del(`/api/console/ab-rules/${id}`);
      setRules((r) => r.filter((x) => x.id !== id));
      onChanged();
    } catch (e) { setError((e as Error).message); }
  }

  const label = (r: typeof rules[number]) =>
    r.user_id
      ? users.find((u) => u.id === r.user_id)?.name ?? "Unknown person"
      : groups.find((g) => g.id === r.group_id)?.name ?? "Unknown group";

  return (
    <Drawer title={`Share ${book.name}`} onClose={onClose}>
      <div className="stack">
        {error ? <Notice>{error}</Notice> : null}
        <table className="data">
          <thead><tr><th>Shared with</th><th>Access</th><th style={{ width: 80 }} /></tr></thead>
          <tbody>
            {rules.map((r) => (
              <tr key={r.id}>
                <td>{label(r)}</td>
                <td className="muted">{RULE_LABEL[r.rule]}</td>
                <td className="actions">
                  <button className="btn btn--danger btn--sm" onClick={() => remove(r.id)}>Remove</button>
                </td>
              </tr>
            ))}
            {!rules.length ? (
              <tr><td colSpan={3} className="faint" style={{ padding: "1rem", textAlign: "center" }}>
                Only the owner can see this book.
              </td></tr>
            ) : null}
          </tbody>
        </table>

        <div className="field">
          <label className="field__label">Share with</label>
          <select value={target} onChange={(e) => setTarget(e.target.value)}>
            <option value="">Choose a person or group</option>
            <optgroup label="Groups">
              {groups.map((g) => <option key={g.id} value={`g:${g.id}`}>{g.name}</option>)}
            </optgroup>
            <optgroup label="People">
              {users.map((u) => <option key={u.id} value={`u:${u.id}`}>{u.display_name || u.name}</option>)}
            </optgroup>
          </select>
        </div>
        <div className="field">
          <label className="field__label">Access</label>
          <select value={rule} onChange={(e) => setRule(Number(e.target.value))}>
            <option value={1}>Read only</option>
            <option value={2}>Read and write</option>
            <option value={3}>Full control</option>
          </select>
        </div>
        <button className="btn btn--primary" onClick={add} disabled={!target}>Share</button>
      </div>
    </Drawer>
  );
}
